import { createComponent } from "react-fela";
import { useState, useEffect, useRef } from "react";
import { expand } from "~/styleguide/utils";
import { colors } from "~/styleguide";
import find from "lodash.find";

import { motion, AnimatePresence } from "framer-motion";

export default function MultiSelect({
    options,
    onChange,
    selected,
    type = "Option"
}) {
    const node = useRef();
    const [isOpen, setIsOpen] = useState(false);

    const isValueSelected = ({ value }) => find(selected, { value: value });

    const toggleValue = option => {
        const found = isValueSelected(option);
        setIsOpen(false);
        if (found) {
            if (onChange)
                onChange(
                    selected.filter(({ value }) => value !== option.value)
                );
        } else {
            if (onChange) onChange([...selected, option]);
        }
    };

    // Set up listeners to close the dropdown when clicking off this component
    // See: https://medium.com/@pitipatdop/little-neat-trick-to-capture-click-outside-with-react-hook-ba77c37c7e82
    useEffect(() => {
        document.addEventListener("mousedown", handleClick);
        return function cleanUp() {
            document.removeEventListener("mousedown", handleClick);
        };
    });

    const handleClick = e => {
        if (!node || !node.current || !node.current.contains(e.target))
            setIsOpen(false);
    };

    return (
        <Container innerRef={node} isOpen={isOpen}>
            {/* Input field (toggles dropdown open) */}
            <InputField onClick={() => setIsOpen(!isOpen)} />

            {/* Selected value label */}
            {renderFieldLabel(selected, type)}

            {/* Dropdown indicator icon */}
            <Indicator
                isOpen={isOpen}
                src="/icons/dropdown-arrow.svg"
            />

            {/* Dropdown container and options */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        style={{ zIndex: 10, position: "relative" }}
                        initial={{ opacity: 0, translateY: -5 }}
                        animate={{ opacity: 1, translateY: 0 }}
                        exit={{ opacity: 0, translateY: 5 }}
                        transition={{ duration: 0.3 }}
                    >
                        <Dropdown>
                            {options &&
                                options.length &&
                                options.map(option => {
                                    return (
                                        <Option
                                            key={option.value}
                                            selected={isValueSelected(option)}
                                            onClick={() => {
                                                toggleValue(option);
                                            }}
                                            dangerouslySetInnerHTML={{
                                                __html: option.label
                                            }}
                                        />
                                    );
                                })}
                        </Dropdown>
                    </motion.div>
                )}
            </AnimatePresence>
        </Container>
    );
}

const renderFieldLabel = (selected, type) => {
    let label = "View All";

    if (selected.length === 1) label = selected[0].label;
    else if (selected.length > 1)
        label = `${selected.length} ${type}s Selected`;

    return (
        <Label
            dangerouslySetInnerHTML={{
                __html: label
            }}
        />
    );
};

const Container = createComponent(({ isOpen }) => ({
    position: "relative",
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: colors.black,
    borderRadius: 6,
    height: 54,
    willChange: "border-color",
    transition: "0.1s border-color ease",

    ":hover": {
        borderColor: colors.blue
    },

    extend: [
        {
            condition: isOpen,
            style: {
                borderColor: colors.blue
            }
        }
    ]
}));

const Dropdown = createComponent(() => ({
    boxShadow: "0px 0px 4px #CCCCCC",
    borderRadius: 6,
    maxHeight: 270,
    overflowY: "scroll",
    position: "absolute",
    zIndex: 10,
    left: -1,
    right: -1,
    top: "calc(100% + 8px)",
    // paddingV: 10,

    // TEMP
    background: "white"
}));

const Label = createComponent(
    () => ({
        lineHeight: "54px",
        paddingLeft: 16,
        width: "calc(100% - 40px)",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
    }),
    "p",
    ["dangerouslySetInnerHTML"]
);

const Indicator = createComponent(
    ({ isOpen }) => ({
        position: "absolute",
        right: 16,
        top: "calc(50% - 3px)",
        display: "block",
        pointerEvents: "none",
        transformOrigin: "center center",
        transition: "0.3s transform ease",
        willChange: "transform",

        extend: [
            {
                condition: isOpen,
                style: {
                    transform: "rotate(-180deg)"
                }
            }
        ]
    }),
    "img",
    ["src"]
);

const checkBox = selected => ({
    display: "block",
    content: "''",
    border: "1px solid #cccccc",
    position: "absolute",
    width: 18,
    height: 18,
    left: 17,
    top: "50%",
    borderRadius: 2,
    transform: "translateY(-50%)",
    willChange: "border, background-color",
    transition: "color 0.1s ease, background-color 0.1s ease",
    backgroundSize: "70%",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center 15%",
    backgroundImage:
        'url("' +
        "data:image/svg+xml,%3Csvg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1.64579 12.0285L6.17127 16.554L16.3536 6.37167' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A" +
        '")',

    extend: [
        {
            condition: selected,
            style: {
                backgroundColor: colors.blue,
                border: `1px solid ${colors.blue}`
            }
        }
    ]
});

const Option = createComponent(
    ({ selected }) => ({
        paddingV: 18,
        paddingLeft: 50,
        cursor: "pointer",
        position: "relative",

        ":hover": {
            background: colors.lightGrey
        },

        ":before": checkBox(selected)
    }),
    "div",
    ["onClick", "dangerouslySetInnerHTML"]
);

const InputField = createComponent(
    () => ({
        position: "relative",
        border: "1px solid black",
        borderRadius: 6,
        height: 54,
        opacity: 0,
        cursor: "pointer",
        ...expand()
    }),
    "div",
    ["onClick"]
);
